/**
 * @fileoverview Pure adapters: API payload of `GET task_track/student` → domain
 * (AR-898). Kept free of React/HTTP so the mapping can be unit-tested.
 *
 * The backend uses `session_meeting` / `tutor` names; the front prefers
 * `lesson` / `teacher`. The rename happens here only.
 */

import { aggregateLessonStatus, resolveTrackStatus } from 'pages/TaskTracker/utils/aggregateLessonStatus'
import type {
  DeliverableStatus,
  StudentTrackerActivity,
  StudentTrackerDelivery,
  StudentTrackerFinal,
  StudentTrackerLesson,
  StudentTrackerMatrix,
  StudentTrackerStat,
  StudentTrackerSummary,
  TrackStatus,
} from '../types'

// ---------------------------------------------------------------------------
// Raw payload (camelCased by the API helper). Local to the service layer.
// ---------------------------------------------------------------------------

export interface RawStudentMatrixActivity {
  id: string
  title: string
  sessionMeetingId: string
  sessionMeetingTitle: string
  sessionMeetingDate: string
  hasAssessments: boolean
  hasTasks: boolean
  /** Backend follow-up of AR-898 — absent until shipped. */
  deadline?: string | null
}

export interface RawStudentMatrixDelivery {
  id: string
  uploadedByTeacher?: boolean
  uploadedById?: string | null
  uploadedByName?: string | null
  submittedAt: string
  workCompletedAt?: string | null
  filesCount?: number
  linksCount?: number
  note?: string | null
  /** Backend follow-up of AR-898 — absent until shipped. */
  late?: boolean
}

export interface RawStudentMatrixCell {
  id: string
  assessmentId: string | null
  selfAssessmentCompleted: boolean
  tutorAssessmentCompleted: boolean
  deliverableCompleted: boolean
  selfAssessmentStatus?: TrackStatus
  tutorAssessmentStatus?: TrackStatus
  deliverable?: RawStudentMatrixDelivery | null
}

export interface RawStudentMatrixStudent {
  id: string
  firstName: string
  lastName: string
  processing?: boolean
  activities: RawStudentMatrixCell[]
  finalAssessment: {
    id: string | null
    selfAssessmentCompleted: boolean
    tutorAssessmentCompleted: boolean
    selfAssessmentStatus?: TrackStatus
    tutorAssessmentStatus?: TrackStatus
  }
}

export interface RawStudentMatrix {
  editable?: boolean
  processing?: boolean
  activities: RawStudentMatrixActivity[]
  finalAssessment?: { enabled?: boolean; present?: boolean }
  students: RawStudentMatrixStudent[]
}

// ---------------------------------------------------------------------------
// Adapters
// ---------------------------------------------------------------------------

const EMPTY_FINAL: RawStudentMatrixStudent['finalAssessment'] = {
  id: null,
  selfAssessmentCompleted: false,
  tutorAssessmentCompleted: false,
}

function adaptDelivery(raw: RawStudentMatrixDelivery): StudentTrackerDelivery {
  return {
    id: raw.id,
    uploadedByTeacher: raw.uploadedByTeacher ?? false,
    uploadedByName: raw.uploadedByName ?? null,
    submittedAt: raw.submittedAt,
    filesCount: raw.filesCount ?? 0,
    linksCount: raw.linksCount ?? 0,
    note: raw.note?.trim() ? raw.note : null,
    ...(raw.late !== undefined && { late: raw.late }),
  }
}

function earliestDate(dates: Array<string | null>): string | null {
  const known = dates.filter((date): date is string => Boolean(date))
  if (known.length === 0) return null
  return known.reduce((earliest, date) => (date < earliest ? date : earliest))
}

/**
 * Groups the activities by lesson and folds the student's cells into one
 * status per lesson. Activity order from the API (lesson date asc, position
 * asc) is preserved, so `lessons[]` is already in display order.
 */
export function buildLessons(
  activities: RawStudentMatrixActivity[],
  cells: RawStudentMatrixCell[],
): StudentTrackerLesson[] {
  const cellByActivity = new Map(cells.map(cell => [cell.id, cell]))
  const lessons: StudentTrackerLesson[] = []
  const byId = new Map<string, { lesson: StudentTrackerLesson; self: TrackStatus[]; teacher: TrackStatus[] }>()

  for (const activity of activities) {
    let entry = byId.get(activity.sessionMeetingId)
    if (!entry) {
      entry = {
        lesson: {
          id: activity.sessionMeetingId,
          title: activity.sessionMeetingTitle,
          date: activity.sessionMeetingDate,
          hasAssessments: false,
          deliverableActivities: [],
          assessmentId: null,
          selfStatus: 'pending',
          teacherStatus: 'pending',
          deliverableStatus: 'pending',
          deliverableLate: false,
          deliverableDeadline: null,
          uploadedByTeacher: false,
          uploadedByName: null,
          teacherNotes: [],
        },
        self: [],
        teacher: [],
      }
      byId.set(activity.sessionMeetingId, entry)
      lessons.push(entry.lesson)
    }

    const cell = cellByActivity.get(activity.id)
    const { lesson } = entry

    if (activity.hasAssessments) {
      lesson.hasAssessments = true
      if (cell) {
        lesson.assessmentId = lesson.assessmentId ?? cell.assessmentId
        entry.self.push(resolveTrackStatus(cell.selfAssessmentStatus, cell.selfAssessmentCompleted))
        entry.teacher.push(resolveTrackStatus(cell.tutorAssessmentStatus, cell.tutorAssessmentCompleted))
      } else {
        entry.self.push('pending')
        entry.teacher.push('pending')
      }
    }

    if (activity.hasTasks) {
      const delivery = cell?.deliverable ? adaptDelivery(cell.deliverable) : null
      const deliverableActivity: StudentTrackerActivity = {
        id: activity.id,
        title: activity.title,
        deadline: activity.deadline ?? null,
        completed: cell?.deliverableCompleted ?? false,
        delivery,
      }
      lesson.deliverableActivities.push(deliverableActivity)
      if (delivery?.uploadedByTeacher) {
        lesson.uploadedByTeacher = true
        lesson.uploadedByName = lesson.uploadedByName ?? delivery.uploadedByName
      }
      if (delivery?.note) lesson.teacherNotes.push(delivery.note)
      if (delivery?.late) lesson.deliverableLate = true
    }
  }

  for (const { lesson, self, teacher } of byId.values()) {
    lesson.selfStatus = aggregateLessonStatus(self)
    lesson.teacherStatus = aggregateLessonStatus(teacher)
    const deliverables = lesson.deliverableActivities
    const allSubmitted = deliverables.length > 0 && deliverables.every(act => act.completed)
    lesson.deliverableStatus = allSubmitted ? 'completed' : 'pending'
    lesson.deliverableDeadline = earliestDate(deliverables.map(act => act.deadline))
  }

  return lessons
}

export function buildFinal(
  raw: RawStudentMatrix['finalAssessment'],
  studentFinal: RawStudentMatrixStudent['finalAssessment'] | undefined,
): StudentTrackerFinal {
  const cell = studentFinal ?? EMPTY_FINAL
  return {
    present: raw?.present ?? false,
    enabled: raw?.enabled ?? false,
    id: cell.id,
    selfStatus: resolveTrackStatus(cell.selfAssessmentStatus, cell.selfAssessmentCompleted),
    teacherStatus: resolveTrackStatus(cell.tutorAssessmentStatus, cell.tutorAssessmentCompleted),
  }
}

function countStat(items: Array<{ counts: boolean; done: boolean }>): StudentTrackerStat {
  return items.reduce<StudentTrackerStat>(
    (stat, item) => (item.counts ? { done: stat.done + (item.done ? 1 : 0), total: stat.total + 1 } : stat),
    { done: 0, total: 0 },
  )
}

/**
 * Totalizers with the prototype's semantics: one unit per lesson (not per
 * activity) plus the final assessment once its phase has started.
 */
export function buildSummary(lessons: StudentTrackerLesson[], final: StudentTrackerFinal): StudentTrackerSummary {
  const finalCounts = final.present && final.enabled
  const isDone = (status: TrackStatus | DeliverableStatus) => status === 'completed'
  return {
    deliverables: countStat(
      lessons.map(lesson => ({
        counts: lesson.deliverableActivities.length > 0,
        done: isDone(lesson.deliverableStatus),
      })),
    ),
    selfAssessments: countStat([
      ...lessons.map(lesson => ({ counts: lesson.hasAssessments, done: isDone(lesson.selfStatus) })),
      { counts: finalCounts, done: isDone(final.selfStatus) },
    ]),
    teacherAssessments: countStat([
      ...lessons.map(lesson => ({ counts: lesson.hasAssessments, done: isDone(lesson.teacherStatus) })),
      { counts: finalCounts, done: isDone(final.teacherStatus) },
    ]),
  }
}

export function adaptStudentMatrix(raw: RawStudentMatrix): StudentTrackerMatrix {
  const student = raw.students?.[0]
  const lessons = buildLessons(raw.activities ?? [], student?.activities ?? [])
  const final = buildFinal(raw.finalAssessment, student?.finalAssessment)
  return {
    studentId: student?.id ?? '',
    studentName: student ? `${student.firstName} ${student.lastName}`.trim() : '',
    editable: raw.editable ?? true,
    processing: Boolean(raw.processing || student?.processing),
    lessons,
    final,
    summary: buildSummary(lessons, final),
  }
}
